import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TechRow } from '../models/sla.models';
import { pctMet, sumCounts } from '../sla-rules';
import { PctChip } from './pct-chip';

/**
 * "SLA % Met by Tech" — the breakdown columns plus the Issue WOs / Request WOs
 * percentage columns. Techs with no tickets this week are kept (as on the
 * sheet) but visually dimmed.
 */
@Component({
  selector: 'app-tech-table',
  imports: [PctChip],
  template: `
    <div class="table-scroll">
      <table class="sla-table">
        <thead>
          <tr>
            <th class="row-label" scope="col">Tech</th>
            <th scope="col" title="Total tickets">Total</th>
            <th scope="col" title="Closed tickets">Closed</th>
            <th scope="col">Met SLA</th>
            <th scope="col">Missed SLA</th>
            <th scope="col" title="Met SLA – WIP">Met WIP</th>
            <th scope="col" title="Missed SLA – WIP">Missed WIP</th>
            <th scope="col">% Met SLA</th>
            <th scope="col">Issue WOs</th>
            <th scope="col">Request WOs</th>
          </tr>
        </thead>
        @if (udtRows().length) {
          <tbody class="grouped-tbody">
            <tr>
              <td class="group-header" colspan="10">UDT Techs</td>
            </tr>
            @for (r of udtRows(); track r.label; let i = $index) {
              <tr [class.zero-row]="r.total === 0" [class.zebra-row]="i % 2 === 1">
                <td class="row-label">{{ r.label }}</td>
                <td class="num">{{ r.total }}</td>
                <td class="num">{{ r.closed }}</td>
                <td class="num">{{ r.met }}</td>
                <td class="num">{{ r.missed }}</td>
                <td class="num">{{ r.metWip }}</td>
                <td class="num">{{ r.missedWip }}</td>
                <td class="pct"><app-pct-chip [pct]="pctMet(r)" /></td>
                <td class="pct"><app-pct-chip [pct]="r.issuePct" /></td>
                <td class="pct"><app-pct-chip [pct]="r.requestPct" /></td>
              </tr>
            }
            <tr>
              <td class="row-label">Subtotal</td>
              <td class="num">{{ udtTotal().total }}</td>
              <td class="num">{{ udtTotal().closed }}</td>
              <td class="num">{{ udtTotal().met }}</td>
              <td class="num">{{ udtTotal().missed }}</td>
              <td class="num">{{ udtTotal().metWip }}</td>
              <td class="num">{{ udtTotal().missedWip }}</td>
              <td class="pct"><app-pct-chip [pct]="pctMet(udtTotal())" /></td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        }
        @if (cnbRows().length) {
          <tbody class="grouped-tbody">
            <tr>
              <td class="group-header" colspan="10">CNB Techs</td>
            </tr>
            @for (r of cnbRows(); track r.label; let i = $index) {
              <tr [class.zero-row]="r.total === 0" [class.zebra-row]="i % 2 === 1">
                <td class="row-label">{{ r.label }}</td>
                <td class="num">{{ r.total }}</td>
                <td class="num">{{ r.closed }}</td>
                <td class="num">{{ r.met }}</td>
                <td class="num">{{ r.missed }}</td>
                <td class="num">{{ r.metWip }}</td>
                <td class="num">{{ r.missedWip }}</td>
                <td class="pct"><app-pct-chip [pct]="pctMet(r)" /></td>
                <td class="pct"><app-pct-chip [pct]="r.issuePct" /></td>
                <td class="pct"><app-pct-chip [pct]="r.requestPct" /></td>
              </tr>
            }
            <tr>
              <td class="row-label">Subtotal</td>
              <td class="num">{{ cnbTotal().total }}</td>
              <td class="num">{{ cnbTotal().closed }}</td>
              <td class="num">{{ cnbTotal().met }}</td>
              <td class="num">{{ cnbTotal().missed }}</td>
              <td class="num">{{ cnbTotal().metWip }}</td>
              <td class="num">{{ cnbTotal().missedWip }}</td>
              <td class="pct"><app-pct-chip [pct]="pctMet(cnbTotal())" /></td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        }
      </table>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TechTable {
  readonly rows = input.required<TechRow[]>();

  protected readonly udtRows = computed(() => this.rows().filter((r) => r.isUdt));
  protected readonly cnbRows = computed(() => this.rows().filter((r) => !r.isUdt));
  protected readonly udtTotal = computed(() => sumCounts(this.udtRows()));
  protected readonly cnbTotal = computed(() => sumCounts(this.cnbRows()));
  protected readonly pctMet = pctMet;
}
